import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ASO_API } from "@/lib/asoEngine";

const statuses = ["all", "new", "contacted", "proposal_sent", "won", "lost"];

export const AsoEngineAdminPanel = () => {
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchReports = async () => {
    const [summaryResponse, reportsResponse] = await Promise.all([
      axios.get(`${ASO_API}/admin/summary`, { withCredentials: true }),
      axios.get(`${ASO_API}/admin/reports`, {
        withCredentials: true,
        params: { search, status: statusFilter === "all" ? "" : statusFilter },
      }),
    ]);
    setSummary(summaryResponse.data);
    setReports(reportsResponse.data);
  };

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const updateStatus = async (reportId, nextStatus) => {
    await axios.put(`${ASO_API}/admin/reports/${reportId}/status`, { lead_status: nextStatus }, { withCredentials: true });
    fetchReports();
  };

  return (
    <div className="space-y-6" data-testid="aso-admin-panel">
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Total Reports Generated", value: summary?.total_reports_generated ?? 0 },
          { label: "Total Leads", value: summary?.total_leads ?? 0 },
          { label: "Conversion Rate", value: `${summary?.conversion_rate ?? 0}%` },
          { label: "Top Industries", value: summary?.top_industries?.[0]?.label || "—" },
        ].map((item, index) => (
          <div key={item.label} className="glitter-card glass-panel rounded-[1.7rem] p-5" data-testid={`aso-admin-stat-${index + 1}`}>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/[0.1] bg-white/[0.06] text-blue-100">
              <Sparkles className="h-5 w-5" />
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-white/[0.42]">{item.label}</p>
            <p className="mt-3 font-display text-4xl text-white">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="editorial-shell glitter-card glass-panel rounded-[2rem] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="section-kicker">ASO Engine Reports</p>
            <h3 className="mt-3 font-display text-3xl text-white">Generated reports and lead qualification</h3>
          </div>

          <a
            href={`${ASO_API}/admin/export`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-5 py-3 text-sm font-medium text-white/82 transition-all duration-300 hover:border-blue-300/30 hover:bg-blue-400/10 hover:text-white"
            data-testid="aso-admin-export-button"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </a>
        </div>

        <div className="mt-6 flex flex-col gap-3 lg:flex-row">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search app, founder, or company"
            className="h-11 flex-1 rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-sm text-white outline-none placeholder:text-white/[0.38]"
            data-testid="aso-admin-search-input"
          />
          <Button type="button" variant="outline" onClick={fetchReports} className="h-11 rounded-2xl border-white/[0.12] bg-white/[0.04] text-white hover:bg-white/[0.08]" data-testid="aso-admin-search-button">
            Refresh
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11 w-full rounded-2xl border-white/[0.08] bg-black/20 text-white lg:w-[220px]" data-testid="aso-admin-status-filter">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => <SelectItem key={status} value={status}>{status === "all" ? "All statuses" : status.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 overflow-hidden rounded-[1.6rem] border border-white/[0.08] bg-black/20">
          <Table data-testid="aso-admin-table" className="text-white">
            <TableHeader>
              <TableRow className="border-white/[0.08] hover:bg-transparent">
                <TableHead className="px-4 py-4 text-white/[0.42]">App</TableHead>
                <TableHead className="px-4 py-4 text-white/[0.42]">Founder / Company</TableHead>
                <TableHead className="px-4 py-4 text-white/[0.42]">Scores</TableHead>
                <TableHead className="px-4 py-4 text-white/[0.42]">Lead</TableHead>
                <TableHead className="px-4 py-4 text-white/[0.42]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.id} className="border-white/[0.08] align-top hover:bg-white/[0.03]" data-testid={`aso-admin-row-${report.id}`}>
                  <TableCell className="px-4 py-4">
                    <p className="font-medium text-white">{report.app_name}</p>
                    <p className="mt-1 text-sm text-white/[0.58]">{report.store_type.replace(/_/g, " ")}</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.2em] text-blue-100">{report.category}</p>
                  </TableCell>
                  <TableCell className="px-4 py-4 text-sm text-white/[0.72]">
                    <p>{report.founder_name}</p>
                    <p className="mt-1 text-white/[0.52]">{report.company_name}</p>
                    <p className="mt-2 text-white/[0.44]">{report.work_email}</p>
                  </TableCell>
                  <TableCell className="px-4 py-4 text-sm text-white/[0.72]">
                    <p>ASO Score: {Math.round(report.dashboard_summary.aso_score.value)}</p>
                    <p className="mt-2 text-white/[0.44]">Lead Score: {report.lead_score}</p>
                    <p className="mt-2 text-white/[0.44]">Growth Potential: {report.growth_potential_percent}%</p>
                  </TableCell>
                  <TableCell className="px-4 py-4 text-sm text-white/[0.72]">
                    <p>{report.qualification?.phone_number || "Qualification pending"}</p>
                    <p className="mt-2 text-white/[0.44]">{report.qualification?.company_size || "No company size yet"}</p>
                    <p className="mt-2 text-white/[0.44]">{report.qualification?.monthly_installs || "No install data yet"}</p>
                  </TableCell>
                  <TableCell className="px-4 py-4">
                    <Badge className="border-white/[0.12] bg-white/[0.06] px-3 py-2 text-white/82">{report.lead_status.replace(/_/g, " ")}</Badge>
                    <Select value={report.lead_status} onValueChange={(value) => updateStatus(report.id, value)}>
                      <SelectTrigger className="mt-3 h-10 rounded-xl border-white/[0.08] bg-black/20 text-white" data-testid={`aso-admin-status-select-${report.id}`}>
                        <SelectValue placeholder="Update status" />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.filter((status) => status !== "all").map((status) => <SelectItem key={status} value={status}>{status.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};